import { createSelector } from '@ngrx/store';
import { BookingTariff } from '@products/models/booking.model';
import { TariffUuids } from '@products/models/tariff.model';
import { areBookedTariffAmanoParkingReservationsValid } from '@store/products/booking/product/amano-parking/amano-parking-booking.selectors';
import { areBookedTariffContingentReservationsValid } from '@store/products/booking/product/contingent/contingent-booking.selectors';
import { areBookedTariffParkingReservationsValid } from '@store/products/booking/product/parking/parking-booking.selectors';
import { getAnonymousTariffs } from '@store/products/booking/product/voucher/voucher-booking.selectors';
import { areBookedTariffWorkshopsValid } from '@store/products/booking/product/workshop/workshop-booking.selectors';
import { getProductBookingState } from '@store/products/product.selectors';

export const selectBookedTariffUuids = createSelector(
  getProductBookingState,
  (productBookingState): TariffUuids => productBookingState.bookedTariffUuids
);

/**
 * Get if anonymous product is sent to api
 * @description Gets is anonymous product sent to API via prepareDataForSaveAndSend method
 * @returns True if anonymous product was sent with prepareDataForSaveAndSend method, otherwise false
 */
export const isAnonymousProductSentToAPI = createSelector(
  getProductBookingState,
  (productBookingState): boolean => productBookingState.isAnonymousProductSentToAPI
);

/**
 * Get claimed ticket hash
 * @description Gets claimed ticket hash
 * @returns Claimed ticket hash
 */
export const getClaimedTicketHash = createSelector(
  getProductBookingState,
  (productBookingState): string => productBookingState.claimedTicketHash
);

/**
 * Gets if claimed ticket hash is valid
 * @description Gets if claimed ticket hash is valid
 * @returns true if claimed ticket hash is valid
 */
export const getIsClaimedTicketHashValid = createSelector(
  getProductBookingState,
  (productBookingState): boolean => productBookingState.isClaimedTicketHashValid
);

/**
 * Check if anonymous product is booked
 * @description Check if anonymous product is booked from tariffs, vouchers and packages
 * @returns Boolean
 */
export const isAnonymousProductBooked = createSelector(
  getAnonymousTariffs,
  (bookedAnonymousTariffs: BookingTariff[]): boolean => !!bookedAnonymousTariffs.length
);

/**
 * Get if anonymous products should be send to API
 * @description Checks if there's anonymous product booked and
 * if it was not sent to API via prepareDataForSaveAndSend method
 * @returns True if anonymous product should be send to API, otherwise false
 */
export const shouldSendAnonymousProductToAPI = createSelector(
  isAnonymousProductBooked,
  isAnonymousProductSentToAPI,
  (isAnonymousProductBooked: boolean, isAnonymousProductSentToAPI: boolean): boolean =>
    isAnonymousProductBooked && !isAnonymousProductSentToAPI
);

/**
 * Are booked tariff reservations valid
 * @description Check if booked tariff workshop, contingent and parking reservations are valid
 * @param isWorkshopsSelectionMandatory
 * @param isWorkshopsSelectionMandatoryForZeroPriceTickets
 * @param isZeroPriceTicket
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Boolean
 */
export const areBookedTariffReservationsValid = (
  isWorkshopsSelectionMandatory: boolean,
  isWorkshopsSelectionMandatoryForZeroPriceTickets: boolean,
  isWorkshopTariff: boolean,
  isZeroPriceTicket: boolean,
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    areBookedTariffWorkshopsValid(
      isWorkshopsSelectionMandatory,
      isWorkshopsSelectionMandatoryForZeroPriceTickets,
      isWorkshopTariff,
      isZeroPriceTicket,
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex
    ),
    areBookedTariffContingentReservationsValid(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    areBookedTariffAmanoParkingReservationsValid(
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex
    ),
    areBookedTariffParkingReservationsValid(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (
      areBookedTariffWorkshopsValid,
      areBookedTariffContingentReservationsValid,
      areBookedTariffAmanoParkingReservationsValid,
      areBookedTariffParkingReservationsValid
    ) =>
      areBookedTariffWorkshopsValid &&
      areBookedTariffContingentReservationsValid &&
      areBookedTariffAmanoParkingReservationsValid &&
      areBookedTariffParkingReservationsValid
  );
